import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'

import { PageName } from '@/enums/pageName'
import { debounce } from '@/utils/functionalUtils'
import { getBMI } from '@/utils/weightUtils'

import { ButtonSpinner, PrimaryButton } from '@/components/buttons/primaryButton'
import { ListWithIcons } from '@/components/listWithIcons'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextHTML } from '@/components/typography'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageComplete } from '@/events/events'

import { TProfilePageProps } from './types'

export const Profile = ({
  pageName,
  pageConfig,
  setLastVisitedItem,
  getItem,
  config,
  bodyTypePageNameKey,
}: TProfilePageProps) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [isLoading, setIsLoading] = useState(false)
  const age = getItem(PageName.age)
  const bodyType = getItem(bodyTypePageNameKey || PageName.physicalBuild)

  const {
    title,
    nextUrl,
    tabTitle,
    buttonName,
    buttonFloating,
    policyBlock = true,

    bmiText,
    pronoun,
    images,
    bmiSection,
    bmiDescriptions,
    personalData,
    footerTitle,
  } = pageConfig

  const bmi = Number(getBMI(getItem)?.toFixed(2))

  const navigate = useNavigate()
  const { t } = useTranslation()

  const pageTitle = typeof title === 'function' ? title() : title
  usePageLoadTracking({
    pageName,
    config,
    user,
    title: tabTitle || pageTitle,
  })

  const nextPage = async () => {
    //setItem(String(pageName), String(selectedOptions))
    setIsLoading(true)
    trackPageComplete(
      pageName,
      [],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )

    await debounce(() => {
      navigate(nextUrl)
      setLastVisitedItem(pageName)
    }, 300)()
  }

  const className = `page info-page page-${pageName}`
  const determineClass = () => {
    if (bmi < 18.5) {
      return 'underweight'
    } else if (bmi >= 18.5 && bmi <= 25) {
      return 'normal'
    } else {
      return 'overweight'
    }
  }

  const getClassForBmiItem = (index: number, bmi: number) => {
    let className = 'bmi-item-description'

    if (index === 0 && bmi < 18.5) {
      className += ' active'
    } else if (index === 1 && bmi >= 18.5 && bmi <= 25) {
      className += ' active'
    } else if (index === 2 && bmi > 25) {
      className += ' active'
    }

    return className
  }
  const normalText = t('profileScreen.normalText', 'Normal') + ' - 21.5'

  return (
    <div className={className}>
      <PageTitle title={pageTitle} />

      <div className="content-wrapper">
        <div className="description-content">
          <div className="title">{bmiText}</div>
          <div className="description">{normalText}</div>
        </div>

        <div className="indicator-container">
          <div className={`value ${determineClass()}`}>
            {pronoun} - {bmi}
          </div>
          <div className="line">
            <div className={`switcher ${determineClass()}`}></div>
          </div>

          <div className="description-container">
            {bmiDescriptions?.map((description, index) => (
              <div
                key={index}
                className={getClassForBmiItem(index, bmi)}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ))}
          </div>
        </div>

        {bmiSection && (
          <div className="healthy-container" key="healthy-section">
            <ListWithIcons {...bmiSection} />
          </div>
        )}

        <div className="skin">
          {(() => {
            const isSlimBodyType = bodyType === '0' || bodyType === '1'

            let bodyUser
            switch (age) {
              case '0':
                bodyUser = (
                  <img
                    src={isSlimBodyType ? images.youngAdult.slim : images.youngAdult.large}
                    alt=""
                  />
                )
                break
              case '1':
                bodyUser = (
                  <img src={isSlimBodyType ? images.adult.slim : images.adult.large} alt="" />
                )
                break
              case '2':
                bodyUser = (
                  <img
                    src={isSlimBodyType ? images.middleAged.slim : images.middleAged.large}
                    alt=""
                  />
                )
                break
              case '3':
                bodyUser = (
                  <img src={isSlimBodyType ? images.elderly.slim : images.elderly.large} alt="" />
                )
                break
              default:
                break
            }
            return bodyUser
          })()}
        </div>

        {personalData && (
          <div className="personal-information" key="personal-information">
            <ListWithIcons {...personalData} />
          </div>
        )}

        <TextHTML className="description-bmi" title={footerTitle} />
      </div>

      <div className={`button-container ${buttonFloating ? 'float' : ''}`}>
        <PrimaryButton disabled={isLoading} onClick={nextPage}>
          {isLoading ? <ButtonSpinner /> : buttonName || 'Continue'}
        </PrimaryButton>
      </div>
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
